import React from 'react';
import { StarIcon } from 'lucide-react';
import { Avatar } from './Avatar';
import { avatarColors } from '../data/coaches';
import type { CoachProfile, Testimonial } from '../data/testimonials';
type TestimonialsProps = {
  testimonials: Testimonial[];
  profiles?: CoachProfile[];
  title?: string;
  subtitle?: string;
  background?: 'dark' | 'mid';
};

/** Real reviews only. Renders nothing while the list is empty. */
export function Testimonials({
  testimonials,
  profiles = [],
  title = 'What coaches are saying',
  subtitle = 'From head coaches, assistants and first-year coaches running school and club track.',
  background = 'dark'
}: TestimonialsProps) {
  if (testimonials.length === 0) return null;
  const bg = background === 'dark' ? 'bg-navy' : 'bg-navy-mid';
  return <section aria-label={title} className={`w-full text-chalk ${bg}`} data-id="element-490" data-mp="v96kw1xa1zki">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:py-20" data-id="element-491" data-mp="v7c85aw5tdri">
        <div className="text-center" data-id="element-492" data-mp="qbeqn195u1d5">
          <p className="text-[11px] font-semibold uppercase tracking-[1.6px] text-hl" data-id="element-493" data-mp="q89701javrot">Coach to coach</p>
          <h2 className="mt-3 font-display text-3xl text-chalk sm:text-5xl" data-id="element-494" data-mp="h8ax9aoyulpn">{title}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm text-chalk/70" data-id="element-495" data-mp="m2yceiy2y3rk">{subtitle}</p>
        </div>

        {profiles.length > 0 && <div className="mt-9 grid gap-6 border-y border-navy-line py-7 sm:grid-cols-2 sm:gap-10" data-id="element-496" data-mp="c6lok1p7uk4q">
            {profiles.map((profile, i) => <figure key={profile.name} className="flex gap-4" data-id="element-497" data-mp="zwy1jqzbj726" data-mpi={i}>
                <Avatar name={profile.name} color={avatarColors[(i + 1) % avatarColors.length]} className="h-12 w-12 flex-none" data-id="element-498" data-mp="14vsiendq6ljn" />
                <div data-id="element-499" data-mp="q63dqi6fbzem">
                  <figcaption data-id="element-500" data-mp="q4jv4kxapc9o">
                    <span className="block font-display text-lg font-bold uppercase leading-tight text-chalk" data-id="element-501" data-mp="v57mx8tclx4y">{profile.name}</span>
                    <span className="text-xs font-semibold uppercase tracking-wider text-hl" data-id="element-502" data-mp="1iuiga58dcp92">{profile.role}</span>
                  </figcaption>
                  <blockquote className="mt-2 text-sm leading-relaxed text-chalk/75" data-id="element-503" data-mp="1nnv0m360m9w0">“{profile.bio}”</blockquote>
                </div>
              </figure>)}
          </div>}

        <div className={`mt-9 grid gap-4 sm:grid-cols-2 ${testimonials.length === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`} data-id="element-504" data-mp="1sh3918rilm2f">
          {testimonials.map((item, i) => <figure key={item.author} className="flex flex-col rounded-2xl border border-navy-line bg-navy-card p-5 shadow-card" data-id="element-505" data-mp="1jhpu40kmg5ab" data-mpi={i}>
              <div className="flex items-center gap-2.5" data-id="element-506" data-mp="1ekzefecqbbpf">
                <Avatar name={item.author} src={item.avatar} color={avatarColors[i % avatarColors.length]} className="h-11 w-11" data-id="element-507" data-mp="19rmw1lyn8154" />
                <span className="flex gap-0.5" role="img" aria-label="Five out of five" data-id="element-508" data-mp="14qz4mz6i5iad">
                  {[0, 1, 2, 3, 4].map(s => <StarIcon key={s} className="h-3.5 w-3.5 fill-hl text-hl" aria-hidden="true" data-id="element-509" data-mp="vswvfwcm7jc0" />)}
                </span>
              </div>
              <blockquote className="mt-3 text-sm leading-relaxed text-chalk/90" data-id="element-510" data-mp="qvwq5nnlakly">“{item.quote}”</blockquote>
              <figcaption className="mt-auto pt-4 text-xs text-chalk/60" data-id="element-511" data-mp="37g0fnckq6m5x">
                <span className="block text-[13px] font-semibold text-chalk" data-id="element-512" data-mp="37e5vi0ha2y2w">{item.author}</span>
                {item.role}
              </figcaption>
            </figure>)}
        </div>
      </div>
    </section>;
}